import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { getWithExpiry } from "../common/util/localstorage";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  // 🔹 Fetch data directly from API
  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_URL_NAME}/maintenances/stats`,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${getWithExpiry("TOKEN")}`,
            },
          }
        );

        if (!response.ok) throw new Error(`Erreur ${response.status}`);
        const data = await response.json();

        // ✅ Ensure defaults if missing
        const cleanedData = {
          status: data.status || {},
          livraisonClient: data.livraisonClient || { livre: 0, nonLivre: 0 },
        };

        setStats(cleanedData);
      } catch (error) {
        console.error("Erreur lors du chargement des statistiques :", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading)
    return <CircularProgress style={{ display: "block", margin: "50px auto" }} />;

  if (!stats)
    return <Typography align="center">Aucune statistique disponible</Typography>;

  const statusList = [
    "en attente",
    "en cours de reparation",
    "reparer",
    "retour",
    "livré",
    "non réparable",
  ];

  const colors = [
    "#1976d2", // en attente
    "#ff9800", // en cours
    "#4caf50", // reparer
    "#9c27b0", // retour
    "#009688", // livré
    "#f44336", // non réparable
  ];

  // 🧮 Calculate delivery stats
  const livreCount = stats.status?.["livré"] || 0;
  const totalCount = statusList.reduce(
    (sum, s) => sum + (stats.status?.[s] || 0),
    0
  );
  const nonLivreCount = Math.max(totalCount - livreCount, 0);

  // 🥧 Pie Chart Data
  const livraisonData = {
    labels: ["Livré", "Non livré"],
    datasets: [
      {
        data: [livreCount, nonLivreCount],
        backgroundColor: ["#4caf50", "#f44336"],
      },
    ],
  };

  // 📊 Bar Chart Data
  const statusData = {
    labels: statusList,
    datasets: [
      {
        label: "Nombre de maintenances",
        data: statusList.map((s) => stats.status?.[s] || 0),
        backgroundColor: colors,
      },
    ],
  };

  return (
    <Box sx={{ padding: 3 }}>
    <Typography
  variant="h3"
  align="center"
  sx={{
    mb: { xs: 2, sm: 3 },
    fontWeight: 800,
    background: "linear-gradient(90deg, #002355, #0081f9)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
    textShadow: "1px 1px 2px rgba(0,0,0,0.2)",
  }}
>
        Tableau de bord des maintenances
      </Typography>

      <Grid container spacing={2}>
        {/* 🟩 Résumé par statut */}
        {statusList.map((status, index) => (
          <Grid item xs={6} sm={4} md={2} key={status}>
            <Card
              sx={{
                textAlign: "center",
                py: 2,
                backgroundColor: "#fafafa",
                boxShadow: 2,
                borderRadius: 3,
                transition: "transform 0.2s ease-in-out",
                "&:hover": { transform: "scale(1.03)" },
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ textTransform: "capitalize", color: "#555" }}
              >
                {status}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: colors[index],
                  fontWeight: "bold",
                  fontSize: "1.6rem",
                }}
              >
                {stats.status?.[status] || 0}
              </Typography>
            </Card>
          </Grid>
        ))}

        {/* 📊 Bar chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                Répartition des maintenances par statut
              </Typography>
              <Box sx={{ height: 260 }}>
                <Bar
                  data={statusData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      y: { beginAtZero: true, ticks: { precision: 0 } },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 🥧 Pie chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                Livraison client
              </Typography>
              <Box sx={{ height: 260 }}>
                <Pie
                  data={livraisonData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: { boxWidth: 15, font: { size: 14 } },
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
